/**
 * Cáscara del portal: header + ruta actual + quién está logueado.
 *
 * El `ApiClient` se crea UNA vez y lee la credencial con un getter, así que
 * loguearse, salir o pegar otro Bearer en Ajustes no obliga a recrear nada ni
 * deja pedidos en vuelo con el token viejo. La precedencia (sesión > token
 * manual de compatibilidad) se decide en un solo lugar: `effectiveToken()`.
 *
 * PUERTA DE LOGIN: el portal NO obliga a loguearse siempre. Muestra el login
 * cuando el API contesta 401 —y sólo entonces—, porque el mismo binario tiene
 * que servir para una federación con padrón y para una LAN de cancha con el API
 * en modo dev abierto (ver `gateToLogin`).
 */
import { useMemo, useState, useSyncExternalStore } from 'react';
import { useT } from './i18n/use-t';
import { Header, useHealth, useIdentity } from './components/Header';
import { Card, Empty } from './components/States';
import { useRoute, useSettings } from './hooks';
import { ApiClient } from './lib/api-client';
import { SessionStore, authRoute, effectiveToken, gateToLogin } from './lib/auth';
import { DEFAULT_COMPETITION_TAB, DEFAULT_MATCH_TAB, DEFAULT_MESA_TAB } from './lib/router';
import type { SettingsStore } from './lib/settings';
import { CompetitionPage } from './pages/CompetitionPage';
import { CompetitionsPage } from './pages/CompetitionsPage';
import { ImportPage } from './pages/ImportPage';
import { LoginPage } from './pages/LoginPage';
import { MatchPage } from './pages/MatchPage';
import { MatchesPage } from './pages/MatchesPage';
import { BenchEntryCard, BenchPage } from './pages/bench/BenchPage';
import { MesaPage } from './pages/mesa/MesaPage';
import { PlannerPage } from './pages/PlannerPage';
import { SeasonPage } from './pages/SeasonPage';
import { SettingsPage } from './pages/SettingsPage';
import { TeamsPage } from './pages/TeamsPage';
import { UsersPage } from './pages/UsersPage';

export function App({ store }: { store: SettingsStore }): JSX.Element {
  const settings = useSettings(store);
  // La sesión vive fuera de React, igual que los settings (mismo patrón de
  // store observable). Se crea una sola vez por montaje de la app.
  const [sessions] = useState(() => new SessionStore());
  const session = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot, sessions.getSnapshot);
  const route = useRoute();
  const t = useT();

  /** true si la sesión se cortó sola (401 sobrevenido): hay que explicarlo. */
  const [expired, setExpired] = useState(false);

  const client = useMemo(
    () =>
      new ApiClient({
        getTarget: () => {
          const current = store.getSnapshot();
          return {
            baseUrl: current.apiUrl,
            token: effectiveToken(sessions.getSnapshot(), current.apiToken).token,
          };
        },
        // Si el API rechaza la credencial en medio del uso (venció, o la
        // revocaron desde el padrón), se corta la sesión acá y el portal vuelve
        // al login con el motivo, en vez de mostrar 401 en cada pestaña.
        onUnauthorized: () => {
          if (sessions.getSnapshot() === null) return;
          sessions.clear();
          setExpired(true);
        },
      }),
    [store, sessions],
  );

  const credential = effectiveToken(session, settings.apiToken);
  const connection = useHealth(client, settings.apiUrl, credential.token);
  const identity = useIdentity(client, settings.apiUrl, credential.token);
  const apiLabel = settings.apiUrl === '' ? t('nav.sameOrigin') : settings.apiUrl;

  const special = authRoute(route.path);
  const gated = gateToLogin(identity.status, route.path);
  const showLogin = special === 'login' || gated;

  const login = (
    <LoginPage
      client={client}
      session={session}
      identity={identity.identity}
      expired={expired}
      onLogin={(next) => {
        setExpired(false);
        sessions.set(next);
      }}
      onLogout={sessions.clear}
    />
  );

  // CONSOLA DE PARTIDO (Fase D): se dibuja a pantalla completa, ANTES del
  // header y **antes de la puerta de login**. Las dos cosas son a propósito:
  //  · la barra de navegación del portal no tiene sentido en una tablet
  //    apoyada en un banco ni en la pantalla de un estadio;
  //  · a la consola se entra con el QR de la mesa, así que el 401 previo al
  //    canje es el estado NORMAL de la primera carga. Mandar esa URL al login
  //    del padrón sería pedirle una cuenta al árbitro del Sub-16, que es
  //    justamente lo que la credencial de partido vino a evitar.
  if (route.name === 'bench') {
    if (route.matchId === undefined) return <BenchEntryCard />;
    return (
      <BenchPage
        client={client}
        matchId={route.matchId}
        {...(route.benchView !== undefined ? { view: route.benchView } : {})}
        query={route.query}
        identity={identity.identity}
        onSession={sessions.set}
        // El VAR es config de ESTE navegador (la LAN de la cancha): el salto al
        // saque de la lista de rallies le habla directo (ADR-017).
        replayUrl={settings.replayUrl}
      />
    );
  }

  return (
    <div className="app">
      <Header
        route={route}
        connection={connection}
        identity={identity}
        apiLabel={apiLabel}
        onLogout={session === null ? null : sessions.clear}
      />
      <main className="screen">
        {showLogin ? login : null}
        {!showLogin && special === 'users' ? (
          <UsersPage client={client} identity={identity.identity} />
        ) : null}
        {!showLogin && special === null ? (
          <>
            {route.name === 'matches' ? <MatchesPage client={client} /> : null}
            {route.name === 'match' && route.matchId ? (
              <MatchPage
                client={client}
                matchId={route.matchId}
                tab={route.tab ?? DEFAULT_MATCH_TAB}
                // El VAR es config de ESTE navegador (la LAN de la cancha), no
                // del backend: la pestaña Clips le habla directo (ADR-017).
                replayUrl={settings.replayUrl}
                replayToken={settings.replayToken}
              />
            ) : null}
            {route.name === 'competitions' ? (
              <CompetitionsPage client={client} identity={identity.identity} />
            ) : null}
            {route.name === 'competition' && route.competitionId ? (
              <CompetitionPage
                client={client}
                competitionId={route.competitionId}
                tab={route.competitionTab ?? DEFAULT_COMPETITION_TAB}
                identity={identity.identity}
              />
            ) : null}
            {/* Mesa de control del acta: la pantalla del anotador. */}
            {route.name === 'mesa' && route.matchId ? (
              <MesaPage
                client={client}
                matchId={route.matchId}
                tab={route.mesaTab ?? DEFAULT_MESA_TAB}
                identity={identity.identity}
              />
            ) : null}
            {route.name === 'season' ? <SeasonPage client={client} /> : null}
            {route.name === 'planner' ? <PlannerPage client={client} /> : null}
            {route.name === 'teams' ? <TeamsPage client={client} /> : null}
            {route.name === 'import' ? (
              <ImportPage client={client} identity={identity.identity} />
            ) : null}
            {route.name === 'settings' ? (
              <SettingsPage
                client={client}
                settings={settings}
                onSave={store.set}
                session={session}
                identity={identity.identity}
                onLogout={sessions.clear}
              />
            ) : null}
            {route.name === 'unknown' ? (
              <Card>
                <Empty>
                  {t('routes.unknownBefore')}
                  <a href="#/matches">{t('routes.unknownLink')}</a>
                  {t('routes.unknownAfter')}
                </Empty>
              </Card>
            ) : null}
          </>
        ) : null}
      </main>
    </div>
  );
}
